<template>
    <div v-if="!!market.name" id="sembo-footer" :class="[footerTheme]">
        <div v-if="showActions" class="actions">
            <ButtonPrimary
                v-if="urlSupport"
                size="l"
                :onClick="
                    () => {
                        logStatsigBookingFooterFaqClick();
                        navigate(urlSupport);
                    }
                ">
                {{ t({ key: 'shared.faq' }) }}
            </ButtonPrimary>

            <ButtonPrimary
                v-if="urlNewsletter"
                size="l"
                :onClick="
                    () => {
                        logStatsigBookingFooterNewsletterClick();
                        navigate(urlNewsletter);
                    }
                ">
                {{ t({ key: 'shared.newsletter' }) }}
            </ButtonPrimary>
        </div>

        <div class="logo">
            <Logo :market="market" :theme="logoTheme" />
        </div>

        <div class="content">
            <ul v-for="(row, index) in information" class="content-list" :key="index">
                <li v-for="(item, itemIndex) in row" :key="itemIndex">
                    <Link v-if="item.href" :href="getLinkUrl(item.href)" :label="item.text" />
                    <span v-else> {{ item.text }} </span>
                </li>
            </ul>
        </div>
        <div class="osano-content" v-if="showCookieConsent">
            <ButtonLink iconPosition="left" icon="Cookie" :onClick="showOsanoDrawer">
                {{ t({ key: 'shared.cookie_settings' }) }}
            </ButtonLink>
        </div>
        <div class="copyright">&copy; {{ currentYear }} {{ market.copyright }}</div>
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, defineComponent, PropType } from 'vue';
    import { Market } from '@shared/config/market/market.models';
    import { LogoTheme, FooterTheme } from '../../Layout.interfaces';
    import {
        logStatsigBookingFooterFaqClick,
        logStatsigBookingFooterNewsletterClick,
    } from '@/tracking/statsig/booking.functions';
    import Logo from '../../Logo/Logo.vue';
    import ButtonPrimary from '@ui/components/Button/ButtonPrimary.vue';
    import ButtonLink from '@ui/components/Button/ButtonLink.vue';
    import Link from '@ui/components/Link/Link.vue';

    export default defineComponent({
        name: 'SemboFooter',
        components: { ButtonPrimary, Logo, Link, ButtonLink },
        props: {
            market: {
                type: Object as PropType<Market>,
                required: true,
            },
            logoTheme: {
                type: String as PropType<LogoTheme>,
                required: true,
                default: 'inverted',
            },
            footerTheme: {
                type: String as PropType<FooterTheme>,
                required: true,
                default: 'primary',
            },
            showActions: {
                type: Boolean,
                default: false,
            },
        },
        methods: {
            t,
            logStatsigBookingFooterFaqClick,
            logStatsigBookingFooterNewsletterClick,
            showOsanoDrawer(): void {
                if (window.Osano) {
                    window.Osano.cm.showDrawer();
                }
            },
        },
        setup(props) {
            const information = computed(() => props.market.footerInfo);
            const currentYear = computed(() => new Date().getFullYear());

            const urlSupport = computed(() => {
                const { locale } = props.market;
                return `https://sembo.freshdesk.com/${locale}/support/home`;
            });

            const urlNewsletter = computed((): string | null => {
                const { links } = props.market;
                return links?.newsletter ?? null;
            });

            const navigate = (url: string | null) => {
                url && window.open(url, '_blank');
            };

            const showCookieConsent = computed(() => window.Osano);

            function getLinkUrl(url: string) {
                if (url.startsWith('/')) {
                    return props.market.homePage + url;
                }
                return url;
            }

            return {
                information,
                currentYear,
                urlSupport,
                urlNewsletter,
                showCookieConsent,
                navigate,
                getLinkUrl,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @use '@/styles/import.scss' as *;

    #sembo-footer {
        @include font-body-m();
        color: var(--color-soft-black);
        padding: var(--spacing-96) var(--spacing-40);

        &.primary {
            background-color: var(--color-sembo-purple);
        }

        &.transparent {
            background-color: transparent;
        }

        > .logo,
        .content,
        .copyright,
        .osano-content {
            display: -ms-flexbox;
            display: flex;
            margin-right: auto;
            margin-left: auto;
            -ms-flex-pack: center;
            justify-content: center;
            max-width: 1120px;
        }

        > .logo,
        > .osano-content,
        > .content {
            margin-bottom: var(--spacing-40);
        }

        > .actions {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: var(--spacing-24);
            margin-bottom: var(--spacing-64);

            button {
                max-width: 300px;
                width: 100%;
            }

            @include up(md) {
                flex-direction: row;

                button {
                    min-width: 225px;
                }
            }
        }

        > .content {
            -ms-flex-direction: column;
            flex-direction: column;

            & > :not(:first-child) {
                margin-bottom: var(--spacing-32);
            }

            & > :last-child {
                margin-bottom: 0;
            }

            & > .content-list {
                margin-top: 0;
                padding: 0;
                list-style: none;
                display: -ms-flexbox;
                display: flex;
                -ms-flex-pack: center;
                justify-content: center;

                &:first-child {
                    list-style: disc;
                    margin-bottom: var(--spacing-48);

                    & > li:first-child {
                        list-style: none;
                    }
                }

                & > li {
                    margin-left: var(--spacing-32);
                    text-align: center;

                    &:first-child {
                        margin-left: 0;
                    }
                }
            }
        }

        & > .copyright {
            color: var(--color-neutral-70);
            text-align: center;
        }
    }

    @media (max-width: 768px) {
        #sembo-footer {
            padding: var(--spacing-96) var(--spacing-16);

            & > .content {
                & > :not(:first-child) {
                    margin-bottom: 0;
                }

                & > .content-list {
                    flex-direction: column;
                    align-items: center;

                    &:first-child {
                        margin-bottom: var(--spacing-40);

                        & > li {
                            list-style: none;
                            margin-bottom: var(--spacing-16);
                        }
                    }

                    & > li {
                        margin-left: 0;
                        margin-bottom: var(--spacing-32);
                    }
                }
            }
        }
    }
</style>
